import { Column, PerformanceData, SuperHeader } from '@blisspointmedia/bpm-types/dist/MetricsTable';
import { AggregatorDatum } from '@blisspointmedia/bpm-types/dist/Metrics';
import { normalizeSourceKey } from '@blisspointmedia/bpm-types/dist/Pacing';
import {
  METRIC_TO_FORMATTER,
  SPEND_OR_REVENUE_FORMATTER,
  toPrettyNumber,
  toPrettyPercent2Decimals
} from '@blisspointmedia/bpm-types/dist/metricFormatUtils';
import { ColumnMetaData, ColumnMetaDataMap } from '@Components/MetricsTable/metrics-table-utils';
import {
  PacingMetric,
  PacingRatioMetric,
  PACING_RATIO_METRICS,
  buildKpiToKey,
  meanRatioGoal,
  num,
  pacingHeaderLabel,
  pacingRatioRecipe,
  ratioOfSums,
  ratioPctToGoal
} from './pacingPresetColumnPicker';
import { getPacingDataFetchKey } from './pacingFetchKeys';

// ── Over-time (Date-grouped) table config ─────────────────────────────────────────
// The over-time view pivots each dimension bucket into a super-header column group with dates as rows
// (see PacingPresetTableDashboard). This module owns BOTH halves of that transform:
//   1. The per-variant column BEHAVIORS, which differ from the summary view: Total-X / Toward are
//      past-only (blank on future), Projected is future-only, and Pacing / Run Rate / Remainder show
//      the lambda's running per-date value in each cell but re-derive a meaningful range value on the
//      totals row (a running value doesn't sum) — see those columns for the per-column derivation.
//   2. buildOverTimePivot — turns the lambda's one-row-per-(bucket × date) payload into the
//      one-row-per-date + bucket-namespaced columns + super-headers MetricsTableWithPresets renders.
//
// "Absent key → render --" is the core convention: the lambda omits Total-X/Toward on future dates and
// Projected/Pacing on past dates, so the getters key off presence (`in fetch`), not a 0 sentinel.

const has = (fetch: Record<string, unknown>, key: string): boolean =>
  fetch[key] !== undefined && fetch[key] !== null;

/** Per-metric over-time goal-variant behaviors, keyed by the base dataVarName ({prefix}_{variant}). */
const makeOverTimeGoalVariantMeta = (
  prefix: PacingMetric,
  metricLabel: string,
  formatter: ColumnMetaData['formatter']
): Record<string, ColumnMetaData> => ({
  [`${prefix}_toward_goal`]: {
    prettyName: pacingHeaderLabel(prefix, 'toward_goal'),
    formatter,
    // Past only — the weighted attributed delivery that day; future rows have no actuals.
    aggregator: (datum: AggregatorDatum) => (has(datum, 'toward') ? num(datum.toward) : null),
    requiredColumns: ['toward'],
    overlayText: ''
  },
  [`${prefix}_projected`]: {
    prettyName: pacingHeaderLabel(prefix, 'projected'),
    formatter,
    // Future only — the toward-based projected daily rate; blank on past dates.
    aggregator: (datum: AggregatorDatum) => (has(datum, 'projected') ? num(datum.projected) : null),
    requiredColumns: ['projected'],
    overlayText: `The amount of projected daily ${metricLabel} that is projected based on the daily average ${metricLabel}. For days that have already occurred this field will show '--'.`
  },
  [`${prefix}_goal`]: {
    prettyName: pacingHeaderLabel(prefix, 'goal'),
    formatter,
    // Daily ideal pace; present on every in-window date (total sums to the full goal).
    aggregator: (datum: AggregatorDatum) => (has(datum, 'goal') ? num(datum.goal) : null),
    requiredColumns: ['goal'],
    overlayText: `The amount of daily budgeted ${metricLabel} for each day. This is the total goal amount prorated at a daily level.`
  },
  [`${prefix}_pct_to_goal`]: {
    prettyName: pacingHeaderLabel(prefix, 'pct_to_goal'),
    formatter: toPrettyPercent2Decimals,
    // (toward on past | projected on future) ÷ daily goal. Past rows carry `toward`, future rows carry
    // `projected` — never both — so their sum is the day's delivery per-cell and the range total.
    aggregator: (datum: AggregatorDatum) => {
      const goal = num(datum.goal);
      return goal > 0 ? (num(datum.toward) + num(datum.projected)) / goal : null;
    },
    requiredColumns: ['toward', 'projected', 'goal'],
    overlayText:
      'The percent of the daily goal that was achieved on the historical day. Future facing days are based on the projected amount.'
  },
  // KNOWN ISSUE (pacing / run_rate / remainder below): these running columns tell a single cell from
  // the totals row by `goal_rows > 1` — MetricsTable runs one aggregator for both and gives no explicit
  // is-totals signal. That fails only when a bucket has exactly ONE active date in range (delivery,
  // goal, or projection): the totals row then sums a single cell, `goal_rows` totals to 1, and the
  // footer renders that date's per-cell value instead of the range-derived total (e.g. 0.00% for a lone
  // future date). No per-row counter can fix it — at one row the totals datum IS that cell. Rare, and
  // inherited from the single-aggregator model (bpm used a separate cell fetchGetter + totals
  // aggregator). Real fix = give MetricsTable an explicit is-totals flag; tracked for follow-up.
  [`${prefix}_pacing`]: {
    prettyName: pacingHeaderLabel(prefix, 'pacing'),
    formatter: toPrettyPercent2Decimals,
    // Per date: the lambda's running cumulative delivery ÷ cumulative goal (past dates only). Per-date
    // pacing can't sum, so on the totals row (goal_rows summed past 1) re-derive range pacing as
    // (Σtoward + Σprojected) ÷ Σgoal.
    aggregator: (datum: AggregatorDatum) => {
      if (num(datum.goal_rows) > 1) {
        const goal = num(datum.goal);
        return goal > 0 ? (num(datum.toward) + num(datum.projected)) / goal : null;
      }
      return has(datum, 'pacing') ? num(datum.pacing) : null;
    },
    requiredColumns: ['pacing', 'goal_rows', 'toward', 'projected', 'goal'],
    overlayText: `The amount of the goal you are projected to accomplish by the end of the time period based on the current actual ${metricLabel}. This only applies to historical days and reflects the state of pacing at the time of the date shown.`
  },
  [`${prefix}_run_rate`]: {
    prettyName: pacingHeaderLabel(prefix, 'run_rate'),
    formatter,
    // Per date: the per-goal required forward rate, lambda-computed (frozen on future dates). The totals
    // row (goal_rows > 1) means the rate over FUTURE dates only — the current required forward rate,
    // which matches the Summary tab — falling back to the mean over all in-window dates once a goal has
    // no future days left.
    aggregator: (datum: AggregatorDatum) => {
      const rows = num(datum.goal_rows);
      if (rows > 1) {
        const futureRows = num(datum.run_rate_future_rows);
        return futureRows > 0
          ? num(datum.run_rate_future) / futureRows
          : num(datum.run_rate) / rows;
      }
      return has(datum, 'run_rate') ? num(datum.run_rate) : null;
    },
    requiredColumns: ['run_rate', 'run_rate_future', 'run_rate_future_rows', 'goal_rows'],
    overlayText: `The amount of daily ${metricLabel} that is needed to achieve the goal. Historical days reflect the scenario during that time (i.e. if you were underpacing early in the month it will show a higher amount).`
  },
  [`${prefix}_remainder`]: {
    prettyName: pacingHeaderLabel(prefix, 'remainder'),
    formatter,
    // Per date: running total goal − cumulative delivery, lambda-computed (clamped at 0). The totals
    // row (goal_rows > 1) re-derives the range remainder as max(0, Σgoal − Σtoward − Σprojected).
    aggregator: (datum: AggregatorDatum) => {
      if (num(datum.goal_rows) > 1) {
        return Math.max(0, num(datum.goal) - num(datum.toward) - num(datum.projected));
      }
      return has(datum, 'remainder') ? Math.max(0, num(datum.remainder)) : null;
    },
    requiredColumns: ['remainder', 'goal_rows', 'goal', 'toward', 'projected'],
    overlayText: `The expected end of goal remainder based on actual and projected ${metricLabel}.`
  }
});

/** Ratio metric over-time behaviors. Actual = that day's raw ratio, shown on every past delivery date
 *  (full range). Towards Goal = that day's ratio but only on goal-overlapping dates (the lambda ships
 *  those component sums under a `toward_` prefix). Goal is the flat target on in-window dates. % to
 *  Goal compares Towards Goal against the goal and, with no projection, stops after the last actual.
 *  Totals are ratio-of-sums (components sum across dates) vs the mean goal. */
const makeOverTimeRatioMeta = (metric: PacingRatioMetric): Record<string, ColumnMetaData> => {
  const formatter = METRIC_TO_FORMATTER[metric];
  const label = metric.toUpperCase();
  const { numerator, denominator } = pacingRatioRecipe(metric);
  const towardNum = `toward_${numerator}`;
  const towardDen = `toward_${denominator}`;
  return {
    [metric]: {
      prettyName: pacingHeaderLabel(metric, 'actual'),
      formatter,
      // That day's raw ratio-of-sums; future dates ship no components → null.
      aggregator: (datum: AggregatorDatum) => ratioOfSums(datum, metric),
      requiredColumns: [numerator, denominator],
      overlayText: `The ${label} for the specific date shown. Future dates show '--'.`
    },
    [`${metric}_toward_goal`]: {
      prettyName: pacingHeaderLabel(metric, 'toward_goal'),
      formatter,
      // Only on goal-overlapping dates (component sums arrive under the toward_ prefix); null otherwise.
      aggregator: (datum: AggregatorDatum) => ratioOfSums(datum, metric, 'toward_'),
      requiredColumns: [towardNum, towardDen],
      overlayText: `The ${label} for the date, on dates covered by a goal.`
    },
    [`${metric}_goal`]: {
      prettyName: pacingHeaderLabel(metric, 'goal'),
      formatter,
      // Mean of the flat target over goal-bearing dates; null (not 0) when no date carries a goal, so
      // the totals footer shows "--" rather than $0.00 — matching the summary ratio Goal column.
      aggregator: (datum: AggregatorDatum) => meanRatioGoal(datum),
      requiredColumns: ['goal', 'goal_rows'],
      overlayText: `The ${label} goal that was input into goal intake.`
    },
    [`${metric}_pct_to_goal`]: {
      prettyName: pacingHeaderLabel(metric, 'pct_to_goal'),
      formatter: toPrettyPercent2Decimals,
      aggregator: (datum: AggregatorDatum) =>
        ratioPctToGoal(metric, ratioOfSums(datum, metric, 'toward_'), meanRatioGoal(datum) ?? 0),
      requiredColumns: [towardNum, towardDen, 'goal', 'goal_rows'],
      overlayText: `The ${label} relative to the goal, on dates covered by a goal. Over 100% means beating the goal.`
    }
  };
};

/** Total-X (raw, un-weighted) over-time behavior: past-populated, blank on future. */
const totalXMeta = (
  field: string,
  prettyName: string,
  formatter: ColumnMetaData['formatter'],
  overlayText: string
): ColumnMetaData => ({
  prettyName,
  formatter,
  // Past-populated, blank on future dates (the bucket omits the field there → null).
  aggregator: (datum: AggregatorDatum) => (has(datum, field) ? num(datum[field]) : null),
  requiredColumns: [field],
  overlayText
});

/** Behaviors keyed by BASE dataVarName; the pivot registers each under its bucket-namespaced key.
 *  Exported for unit tests (the per-cell vs totals-row aggregator behavior). */
export const overTimeBaseMetaMap: ColumnMetaDataMap = {
  spend: totalXMeta(
    'spend',
    pacingHeaderLabel('spend', 'actual'),
    SPEND_OR_REVENUE_FORMATTER,
    "The amount of Spend that occurred during the specific date shown. If the date has not happened yet or we do not have data for it, it will show as '--'."
  ),
  revenue: totalXMeta(
    'revenue',
    pacingHeaderLabel('revenue', 'actual'),
    SPEND_OR_REVENUE_FORMATTER,
    "The amount of Revenue that occurred during the specific date shown. If the date has not happened yet or we do not have data for it, it will show as '--'."
  ),
  kpi_volume: totalXMeta(
    'kpi_volume',
    pacingHeaderLabel('volume', 'actual'),
    toPrettyNumber,
    "The amount of KPI Volume that occurred during the specific date shown. If the date has not happened yet or we do not have data for it, it will show as '--'."
  ),
  // Delivery-only actuals (no goal) — read straight off the shared delivery bucket, past-only.
  impressions: totalXMeta(
    'impressions',
    pacingHeaderLabel('impressions', 'actual'),
    toPrettyNumber,
    "The amount of Impressions that occurred during the specific date shown. If the date has not happened yet or we do not have data for it, it will show as '--'."
  ),
  clicks: totalXMeta(
    'clicks',
    pacingHeaderLabel('clicks', 'actual'),
    toPrettyNumber,
    "The amount of Clicks that occurred during the specific date shown. If the date has not happened yet or we do not have data for it, it will show as '--'."
  ),
  ...makeOverTimeGoalVariantMeta('spend', 'Spend', SPEND_OR_REVENUE_FORMATTER),
  ...makeOverTimeGoalVariantMeta('revenue', 'Revenue', SPEND_OR_REVENUE_FORMATTER),
  ...makeOverTimeGoalVariantMeta('volume', 'KPI Volume', toPrettyNumber),
  ...PACING_RATIO_METRICS.reduce(
    (acc, metric) => ({ ...acc, ...makeOverTimeRatioMeta(metric) }),
    {} as Record<string, ColumnMetaData>
  )
};

// ── Pivot builder ─────────────────────────────────────────────────────────────────
// The lambda emits one row per (bucket × date). MetricsTableWithPresets renders one row per
// performanceData row, so to get the Figma layout (dates down, dimension buckets as super-header
// column groups) we transform here: collapse to one row per date, namespace each bucket's fetch
// sub-buckets under `${bucketId}<NS>${sub}`, and cross the selected variant columns with each bucket.

const COL_SEP = '@@'; // dataVarName = `${bucketId}@@${columnIndex}@@${baseDataVarName}`
const NS_SEP = '::'; // fetch key   = `${bucketId}::${subBucket}`

interface OverTimePivot {
  data: PerformanceData[];
  dataColumns: Column[];
  superHeaders: SuperHeader[];
  columnMetaDataMap: ColumnMetaDataMap;
  dataFetchKey: (col: Column | undefined) => string;
}

/**
 * Pivot the lambda's per-(bucket × date) rows into the MetricsTableWithPresets shape: one row per date,
 * dimension buckets as super-header column groups, the chosen variant columns repeated per bucket.
 * `selectedColumns` are the applied preset's data columns — their `dataVarName`s become the base
 * variants (e.g. "spend","spend_projected","spend_goal",…) and their per-column `decimals` carry the
 * user's formatting through to the synthetic columns; `kpi` / `pageSources` resolve the fetch buckets
 * for Global columns.
 */
export const buildOverTimePivot = (
  rows: PerformanceData[],
  selectedColumns: {
    dataVarName: string;
    decimals?: number;
    kpi?: string;
    source?: string;
    label?: string;
  }[],
  kpi: string,
  pageSources: string[]
): OverTimePivot => {
  const pageKpiKey = buildKpiToKey(kpi || '');
  const pageSourceKey = normalizeSourceKey(pageSources);
  // One routing implementation for both views: the summary fetch key resolves each column's
  // sub-bucket; the pivot namespaces it under the row's bucket id.
  const subBucketForColumn = getPacingDataFetchKey(pageKpiKey, pageSourceKey);

  // Stable bucket id + display label from each row's non-Date dimensions.
  const bucketIdByKey = new Map<string, string>();
  const bucketLabelById = new Map<string, string>();
  const bucketOrder: string[] = [];
  const bucketKeyOf = (dims: Record<string, string>): { id: string; label: string } => {
    const entries = Object.entries(dims).filter(([dim]) => dim !== 'Date');
    // JSON-encode the entries for identity — a delimiter join would collapse distinct buckets when a
    // dimension value (e.g. a custom-segment label) contains the delimiter.
    const key = JSON.stringify(entries);
    let id = bucketIdByKey.get(key);
    if (id === undefined) {
      id = `b${bucketOrder.length}`;
      bucketIdByKey.set(key, id);
      bucketLabelById.set(
        id,
        entries
          .map(([, v]) => v)
          .filter(Boolean)
          .join(', ') || '—'
      );
      bucketOrder.push(id);
    }
    return { id, label: bucketLabelById.get(id) as string };
  };

  // Collapse to one row per date; merge each bucket's sub-bucket fetches under namespaced keys.
  const byDate = new Map<string, Record<string, Record<string, string | number>>>();
  const dateOrder: string[] = [];
  for (const row of rows) {
    const date = String(row.dimensions.Date ?? '');
    const { id } = bucketKeyOf(row.dimensions);
    let fetches = byDate.get(date);
    if (!fetches) {
      fetches = {};
      byDate.set(date, fetches);
      dateOrder.push(date);
    }
    const rowFetches = row.fetches as unknown as Record<string, Record<string, string | number>>;
    for (const [sub, values] of Object.entries(rowFetches)) {
      fetches[`${id}${NS_SEP}${sub}`] = values;
    }
  }

  const data: PerformanceData[] = dateOrder.sort().map(
    date =>
      ({
        dimensions: { Date: date },
        fetches: byDate.get(date) ?? {}
      }) as unknown as PerformanceData
  );

  // Only variants with an over-time behavior render here — a shared preset may include summary-only
  // variants (e.g. Yesterday's Towards Goal) that have no over-time meaning; those are dropped, not shown empty.
  const overTimeCols = selectedColumns.filter(c => overTimeBaseMetaMap[c.dataVarName]);

  // Columns = each bucket × each over-time column instance; super-headers span each bucket's block.
  // Synthetic dataVarNames carry the source-column index so two same-variant columns with different
  // KPIs stay distinct; fetch keys resolve through fetchKeyBySyntheticVar.
  const dataColumns: Column[] = [];
  const superHeaders: SuperHeader[] = [];
  const columnMetaDataMap: ColumnMetaDataMap = {};
  const fetchKeyBySyntheticVar: Record<string, string> = {};
  for (const bucketId of bucketOrder) {
    superHeaders.push({
      data: {
        text: bucketLabelById.get(bucketId) as string,
        leftDivider: true,
        rightDivider: false
      },
      span: overTimeCols.length
    });
    overTimeCols.forEach((c, i) => {
      const dataVarName = `${bucketId}${COL_SEP}${i}${COL_SEP}${c.dataVarName}`;
      const baseMeta = overTimeBaseMetaMap[c.dataVarName];
      // Skip a column with no over-time meta rather than crash on baseMeta.prettyName. Note this
      // silently drops the *_yesterday columns on the Over Time view — "yesterday" is a single
      // snapshot with no per-date series, so it has no over-time meta by design.
      if (!baseMeta) {
        return;
      }
      columnMetaDataMap[dataVarName] = baseMeta;
      fetchKeyBySyntheticVar[dataVarName] = `${bucketId}${NS_SEP}${subBucketForColumn(c)}`;
      dataColumns.push({
        id: dataVarName,
        dataVarName,
        // The source column's label carries the KPI name; fall back to the variant display name.
        label: c.label ?? baseMeta.prettyName,
        // Carry the source preset column's decimals so the over-time cells honor the formatter
        // (constructTableData reads dataHeader.decimals).
        decimals: c.decimals
      });
    });
  }

  const dataFetchKey = (col: Column | undefined): string =>
    col ? (fetchKeyBySyntheticVar[col.dataVarName] ?? '') : '';

  return { data, dataColumns, superHeaders, columnMetaDataMap, dataFetchKey };
};
